import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { toast } from "sonner";
import { ArrowLeft, Sparkle, ImageSquare, Warning, Plus, Trash, GraduationCap } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CountrySelect from "@/components/CountrySelect";
import MemorialAssistant from "@/components/MemorialAssistant";

const TONES = ["Traditional", "Warm", "Concise", "Academic", "Community Tribute", "Formal", "Personal"];
const ROLES = ["Student", "Teacher", "Professor", "Administrator", "Staff", "Alumni", "Coach", "Other"];
const INST_TYPES = ["School", "College", "University", "Vocational", "Training", "Other"];
const SOURCE_TYPES = ["Family member", "Close friend", "Classmate", "Teacher", "Alumni association",
  "Institution", "Published obituary", "Funeral home / memorial notice", "Newspaper", "Official announcement", "Other"];

async function uploadPhoto(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.id;
}

const emptyEdu = { institution: "", institution_type: "", campus: "", department: "", course: "", class_name: "", batch: "", graduation_year: "", years_attended: "", role: "" };

export default function MemorialCreate() {
  useSeo("Create a Memorial", "Respectfully remember a member of your educational community.");
  const { user } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("quick");
  const [f, setF] = useState({
    name: "", preferred_name: "", former_name: "", photo_file_id: null, dob: "", dop: "",
    country: "", city: "", gender: "", intro: "", institution: "", class_batch: "", graduation_year: "",
    profession: "", career: "", achievements: "", contributions: "", interests: "", bio: "", memory: "", epitaph: "",
    verification_relationship: "", verification_source_type: "", verification_source_url: "",
  });
  const [education, setEducation] = useState([]);
  const [ai, setAi] = useState({ tone: "Warm", docType: "obituary", used: false });
  const [dupes, setDupes] = useState([]);
  const [ackRights, setAckRights] = useState(false);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  if (!user) {
    return (
      <div className="mx-auto max-w-md px-5 py-20 text-center" data-testid="memorial-create-authwall">
        <p className="text-muted-foreground">Please <Link to="/login" className="text-secondary hover:underline">sign in</Link> to create a memorial.</p>
      </div>
    );
  }

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { const pid = await uploadPhoto(file); setF((p) => ({ ...p, photo_file_id: pid })); toast.success("Photo attached"); }
    catch { toast.error("Upload failed"); }
  };

  const checkDupes = async () => {
    if (!f.name.trim()) return;
    try {
      const { data } = await api.post("/memorials/check-duplicates", { name: f.name, institution: f.institution, class_batch: f.class_batch });
      setDupes(data.duplicates || []);
    } catch { /* silent */ }
  };

  const editEdu = (i, k, v) => setEducation((rows) => rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  const applyAssistant = (ans) => {
    setF((p) => ({ ...p, ...Object.fromEntries(Object.entries(ans).filter(([, v]) => v && v.trim())) }));
    toast.success("Answers added to the form — review, generate a draft, then publish");
  };

  const runAi = async () => {
    if (!f.name.trim()) { toast.error("Add a name first"); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/memorials/ai-draft", {
        doc_type: ai.docType, tone: ai.tone, name: f.name, preferred_name: f.preferred_name,
        dob: f.dob, dop: f.dop, institution: f.institution, class_batch: f.class_batch,
        graduation_year: f.graduation_year, education, profession: f.profession, career: f.career,
        achievements: f.achievements, contributions: f.contributions, interests: f.interests, memory: f.memory,
      });
      setF((p) => ({ ...p, bio: data.draft }));
      setAi((a) => ({ ...a, used: true }));
      toast.success("AI draft ready — please review & edit before publishing");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI draft failed"); }
    finally { setAiLoading(false); }
  };

  const submit = async (action) => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    if (action === "publish" && !ackRights) { toast.error("Please confirm you have the right to publish this content"); return; }
    setLoading(true);
    try {
      const payload = {
        ...f, education: education.filter((e) => Object.values(e).some(Boolean)),
        ai_generated: ai.used, ai_assisted: ai.used, action,
      };
      const { data } = await api.post("/memorials", payload);
      if (data.forced_review) {
        toast.info("This memorial matches an active member — sent to our team for review before publishing.");
        nav("/memorials");
      } else if (data.state === "published") {
        toast.success("Memorial published");
        nav(`/memorials/${data.id}`);
      } else {
        toast.success(action === "draft" ? "Saved as draft" : "Submitted for review");
        nav("/memorials");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
    finally { setLoading(false); }
  };

  return (
    <div className="mx-auto max-w-2xl px-5 py-12" data-testid="memorial-create-page">
      <Link to="/memorials" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> Obituaries &amp; Memorials</Link>
      <h1 className="mt-4 font-heading text-3xl font-light tracking-tight">Create a Memorial</h1>
      <p className="mt-2 text-sm text-muted-foreground">Remember the people who shaped our journey. Please use only accurate information you have the right to share. AI drafts must be reviewed before publishing.</p>

      <div className="mt-6 grid grid-cols-2 gap-2 rounded-full border border-border/60 p-1" data-testid="memorial-mode-toggle">
        {[["quick", "Quick Memorial"], ["advanced", "Advanced"]].map(([v, l]) => (
          <button key={v} onClick={() => setMode(v)} data-testid={`mode-${v}`}
            className={`rounded-full px-3 py-2 text-sm font-medium transition-colors ${mode === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
        ))}
      </div>

      <div className="mt-6 space-y-6">
        {/* Person */}
        <section className="space-y-4 rounded-2xl border border-border/60 bg-card p-5">
          <h2 className="font-heading text-lg font-medium">Person</h2>
          <div className="flex items-center gap-4">
            <div className="h-20 w-20 shrink-0 overflow-hidden rounded-full ring-2 ring-secondary/20">
              {f.photo_file_id ? <img src={fileUrl(f.photo_file_id)} alt="" className="h-full w-full object-cover" /> : <span className="grid h-full w-full place-items-center bg-accent text-2xl font-heading text-primary">{(f.name || "?")[0]}</span>}
            </div>
            <label className="flex cursor-pointer items-center gap-1.5 text-sm text-secondary hover:underline" data-testid="memorial-photo-label">
              <ImageSquare size={18} /> Upload photograph <input type="file" accept="image/*" className="hidden" onChange={onPhoto} />
            </label>
          </div>
          <div className="space-y-1.5">
            <Label>Full name <span className="text-secondary">*</span></Label>
            <Input value={f.name} onChange={set("name")} onBlur={checkDupes} data-testid="memorial-name" placeholder="e.g. Eleanor Voss" />
          </div>
          {dupes.length > 0 && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="memorial-dupe-warning">
              <p className="flex items-center gap-1.5 font-medium"><Warning size={15} /> We may already have a memorial for this person</p>
              <div className="mt-2 space-y-1">
                {dupes.slice(0, 4).map((d) => (
                  <Link key={d.id} to={`/memorials/${d.id}`} className="block text-xs hover:underline">• {d.name}{d.institution ? ` — ${d.institution}` : ""}{d.class_batch ? ` (${d.class_batch})` : ""}</Link>
                ))}
              </div>
            </div>
          )}
          {mode === "advanced" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Preferred name</Label><Input value={f.preferred_name} onChange={set("preferred_name")} data-testid="memorial-preferred" /></div>
              <div className="space-y-1.5"><Label>Former / maiden name</Label><Input value={f.former_name} onChange={set("former_name")} data-testid="memorial-former" /></div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Year of birth</Label><Input value={f.dob} onChange={set("dob")} data-testid="memorial-dob" placeholder="1951" /></div>
            <div className="space-y-1.5"><Label>Year of passing</Label><Input value={f.dop} onChange={set("dop")} data-testid="memorial-dop" placeholder="2024" /></div>
          </div>
          {mode === "advanced" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Country</Label>
                <CountrySelect value={f.country} onChange={(v) => setF({ ...f, country: v })} testId="memorial-country" />
              </div>
              <div className="space-y-1.5"><Label>City / region</Label><Input value={f.city} onChange={set("city")} data-testid="memorial-city" /></div>
            </div>
          )}
        </section>

        {/* Education */}
        <section className="space-y-4 rounded-2xl border border-border/60 bg-card p-5">
          <h2 className="font-heading text-lg font-medium">Educational connection</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="space-y-1.5 sm:col-span-3"><Label>Institution</Label><Input value={f.institution} onChange={set("institution")} data-testid="memorial-institution" placeholder="Riverside College" /></div>
            <div className="space-y-1.5"><Label>Class / batch</Label><Input value={f.class_batch} onChange={set("class_batch")} data-testid="memorial-classbatch" placeholder="1972" /></div>
            <div className="space-y-1.5"><Label>Graduation year</Label><Input value={f.graduation_year} onChange={set("graduation_year")} data-testid="memorial-gradyear" placeholder="1972" /></div>
          </div>
          {mode === "advanced" && (
            <div className="space-y-3">
              {education.map((e, i) => (
                <div key={i} className="rounded-xl border border-border/50 bg-muted/20 p-3" data-testid={`edu-entry-${i}`}>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><GraduationCap size={14} /> Institution {i + 1}</span>
                    <button onClick={() => setEducation((r) => r.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive" data-testid={`edu-remove-${i}`}><Trash size={14} /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={e.institution} onChange={(ev) => editEdu(i, "institution", ev.target.value)} placeholder="Institution" />
                    <Select value={e.institution_type} onValueChange={(v) => editEdu(i, "institution_type", v)}>
                      <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
                      <SelectContent>{INST_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input value={e.class_name} onChange={(ev) => editEdu(i, "class_name", ev.target.value)} placeholder="Class" />
                    <Input value={e.graduation_year} onChange={(ev) => editEdu(i, "graduation_year", ev.target.value)} placeholder="Graduation year" />
                    <Select value={e.role} onValueChange={(v) => editEdu(i, "role", v)}>
                      <SelectTrigger><SelectValue placeholder="Role" /></SelectTrigger>
                      <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input value={e.years_attended} onChange={(ev) => editEdu(i, "years_attended", ev.target.value)} placeholder="Years attended" />
                  </div>
                </div>
              ))}
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => setEducation((r) => [...r, { ...emptyEdu }])} data-testid="edu-add"><Plus size={14} className="mr-1" /> Add another institution</Button>
            </div>
          )}
        </section>

        {mode === "advanced" && (
          <section className="space-y-4 rounded-2xl border border-border/60 bg-card p-5">
            <h2 className="font-heading text-lg font-medium">Life &amp; career</h2>
            <div className="space-y-1.5"><Label>Profession</Label><Input value={f.profession} onChange={set("profession")} data-testid="memorial-profession" /></div>
            <div className="space-y-1.5"><Label>Career</Label><Textarea value={f.career} onChange={set("career")} className="min-h-[60px]" data-testid="memorial-career" /></div>
            <div className="space-y-1.5"><Label>Achievements &amp; awards</Label><Textarea value={f.achievements} onChange={set("achievements")} className="min-h-[60px]" data-testid="memorial-achievements" /></div>
            <div className="space-y-1.5"><Label>Community contributions</Label><Textarea value={f.contributions} onChange={set("contributions")} className="min-h-[60px]" data-testid="memorial-contributions" /></div>
          </section>
        )}

        {/* Memory */}
        <section className="space-y-3 rounded-2xl border border-border/60 bg-card p-5">
          <h2 className="font-heading text-lg font-medium">Memory</h2>
          <p className="text-sm text-muted-foreground">What would you like people to remember about this person?</p>
          <Textarea value={f.memory} onChange={set("memory")} className="min-h-[90px]" data-testid="memorial-memory" placeholder="A story, a moment, what they meant to the community…" />
          <div className="space-y-1.5"><Label>Short remembrance / epitaph (optional)</Label><Input value={f.epitaph} onChange={set("epitaph")} data-testid="memorial-epitaph" placeholder="Forever part of our story" /></div>
        </section>

        {/* AI draft */}
        <section className="space-y-3 rounded-2xl border border-violet-200 bg-violet-50/50 p-5" data-testid="memorial-ai-section">
          <h2 className="flex items-center gap-2 font-heading text-lg font-medium"><Sparkle size={18} className="text-violet-600" weight="fill" /> AI-assisted draft</h2>
          <p className="text-sm text-muted-foreground">AI writes a respectful draft using <strong>only</strong> the details above — it never invents facts. You must review it before publishing.</p>
          <div className="flex flex-wrap items-center gap-2">
            <MemorialAssistant onApply={applyAssistant} />
            <span className="text-xs text-muted-foreground">New to this? Let the assistant ask you a few simple questions.</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={ai.docType} onValueChange={(v) => setAi({ ...ai, docType: v })}>
                <SelectTrigger data-testid="ai-doctype"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="obituary">Obituary</SelectItem><SelectItem value="biography">Memorial biography</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Tone</Label>
              <Select value={ai.tone} onValueChange={(v) => setAi({ ...ai, tone: v })}>
                <SelectTrigger data-testid="ai-tone"><SelectValue /></SelectTrigger>
                <SelectContent>{TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <Button variant="outline" onClick={runAi} disabled={aiLoading} className="rounded-full" data-testid="ai-generate-btn">
            <Sparkle size={15} className="mr-1.5" /> {aiLoading ? "Writing…" : f.bio ? "Regenerate draft" : "Generate draft"}
          </Button>
          <div className="space-y-1.5">
            <Label>Biography {ai.used && <span className="text-xs text-violet-600">(AI-assisted — review & edit)</span>}</Label>
            <Textarea value={f.bio} onChange={set("bio")} className="min-h-[140px]" data-testid="memorial-bio" placeholder="Write or generate a biography…" />
          </div>
        </section>

        {/* Verification */}
        <section className="space-y-4 rounded-2xl border border-border/60 bg-card p-5">
          <h2 className="font-heading text-lg font-medium">Verification</h2>
          <p className="text-sm text-muted-foreground">How do you know this person has passed away? This is not shown publicly.</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Your relationship</Label><Input value={f.verification_relationship} onChange={set("verification_relationship")} data-testid="verify-relationship" placeholder="Classmate, family, teacher…" /></div>
            <div className="space-y-1.5">
              <Label>Source</Label>
              <Select value={f.verification_source_type} onValueChange={(v) => setF({ ...f, verification_source_type: v })}>
                <SelectTrigger data-testid="verify-source"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{SOURCE_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Supporting link (optional)</Label><Input value={f.verification_source_url} onChange={set("verification_source_url")} data-testid="verify-url" placeholder="https://…" /></div>
        </section>

        {/* Confirm */}
        <section className="space-y-4 rounded-2xl border border-border/60 bg-card p-5">
          <label className="flex items-start gap-2.5 text-sm">
            <Checkbox checked={ackRights} onCheckedChange={(v) => setAckRights(!!v)} data-testid="ack-rights" className="mt-0.5" />
            <span>I have made a reasonable effort to ensure this information is accurate, and I have the right or appropriate permission to submit the included content. Any AI-generated content has been reviewed.</span>
          </label>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => submit("draft")} disabled={loading} data-testid="save-draft-btn">Save draft</Button>
            <Button variant="outline" className="rounded-full" onClick={() => submit("submit")} disabled={loading} data-testid="submit-review-btn">Submit for review</Button>
            <Button className="rounded-full" onClick={() => submit("publish")} disabled={loading} data-testid="publish-btn">{loading ? "Saving…" : "Publish memorial"}</Button>
          </div>
        </section>
      </div>
    </div>
  );
}
